import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import { LoadingSpinner, AccessibleButton, FormInput } from '../ui';
import {
  UserIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CreditCardIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PrinterIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  BeakerIcon,
  GiftIcon
} from '@heroicons/react/24/outline';

// Helper function to sort items - main products first, their testers directly below
const sortOrderItems = (items) => {
  if (!items || items.length === 0) return [];
  
  // Separate testers from regular items
  const testers = [];
  const regularItems = [];
  
  items.forEach(item => {
    const isTester = item.notes && (item.notes.includes('טסטר') || item.notes.includes('בונוס'));
    if (isTester) {
      testers.push(item);
    } else {
      regularItems.push(item);
    }
  });
  
  // Build sorted list: for each regular item, add it then add its matching tester
  const sortedItems = [];
  const usedTesters = new Set();
  
  regularItems.forEach(mainItem => {
    sortedItems.push(mainItem);
    
    // Find matching tester (same product_id or similar product name)
    const mainProductName = (mainItem.product?.name || mainItem.product_name || '').toLowerCase();
    const mainProductId = mainItem.product_id;
    
    testers.forEach(tester => {
      if (usedTesters.has(tester.id)) return;
      
      const testerProductId = tester.product_id;
      const testerProductName = (tester.product?.name || tester.product_name || '').toLowerCase();
      
      // Match by product_id or by similar name
      if (mainProductId === testerProductId || 
          mainProductName === testerProductName ||
          mainProductName.includes(testerProductName.replace('טסטר', '').trim()) ||
          testerProductName.includes(mainProductName)) {
        sortedItems.push(tester);
        usedTesters.add(tester.id);
      }
    });
  });
  
  // Add any remaining testers that weren't matched
  testers.forEach(tester => {
    if (!usedTesters.has(tester.id)) {
      sortedItems.push(tester);
    }
  });
  
  return sortedItems;
};

const OrderDetails = ({ isOpen, onClose, order, onOrderUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Print Order Function
  const handlePrintOrder = () => {
    if (!order) return;

    // Create printable content
    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>הזמנה ${order.order_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap');
          
          body {
            font-family: 'Rubik', Arial, sans-serif;
            direction: rtl;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            color: #000;
          }
          
          .header {
            text-align: center;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .header h1 {
            font-size: 28px;
            font-weight: 700;
            margin: 0 0 10px 0;
          }
          
          .order-number {
            font-size: 18px;
            font-weight: 600;
            color: #2563eb;
          }
          
          .section {
            margin-bottom: 25px;
            padding: 15px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
          }
          
          .section-title {
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 10px;
            color: #1f2937;
            border-bottom: 2px solid #f3f4f6;
            padding-bottom: 5px;
          }
          
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px dashed #e5e7eb;
          }
          
          .info-row:last-child {
            border-bottom: none;
          }
          
          .label {
            font-weight: 600;
            color: #4b5563;
          }
          
          .value {
            color: #111827;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          
          th {
            background-color: #f3f4f6;
            padding: 12px;
            text-align: right;
            font-weight: 700;
            border: 1px solid #d1d5db;
          }
          
          td {
            padding: 10px;
            border: 1px solid #e5e7eb;
            text-align: right;
          }
          
          .total-row {
            font-weight: 700;
            background-color: #f9fafb;
          }
          
          .grand-total {
            background-color: #2563eb;
            color: white;
            font-size: 18px;
          }
          
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #6b7280;
            border-top: 1px solid #e5e7eb;
            padding-top: 20px;
          }
          
          @media print {
            body {
              padding: 20px;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🧾 הזמנה</h1>
          <div class="order-number">הזמנה מספר: ${order.order_number}</div>
          <div style="font-size: 14px; color: #6b7280; margin-top: 10px;">
            תאריך: ${new Date(order.created_at).toLocaleDateString('he-IL', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>

        ${order.company ? `
          <!-- B2B - Company Details -->
          <div class="section">
            <div class="section-title">📌 פרטי חברה</div>
            <div class="info-row">
              <span class="label">שם החברה:</span>
              <span class="value">${order.company.name || '-'}</span>
            </div>
            <div class="info-row">
              <span class="label">ח.פ:</span>
              <span class="value">${order.company.business_number || '-'}</span>
            </div>
            <div class="info-row">
              <span class="label">טלפון:</span>
              <span class="value">${order.company.phone || '-'}</span>
            </div>
            <div class="info-row">
              <span class="label">אימייל:</span>
              <span class="value">${order.company.email || '-'}</span>
            </div>
          </div>
        ` : order.user ? `
          <!-- B2C - Customer Details -->
          <div class="section">
            <div class="section-title">👤 פרטי לקוח</div>
            <div class="info-row">
              <span class="label">שם:</span>
              <span class="value">${order.user.first_name || ''} ${order.user.last_name || ''}</span>
            </div>
            <div class="info-row">
              <span class="label">טלפון:</span>
              <span class="value">${order.user.phone || '-'}</span>
            </div>
            <div class="info-row">
              <span class="label">אימייל:</span>
              <span class="value">${order.user.email || '-'}</span>
            </div>
          </div>
        ` : ''}

        ${order.shippingAddress ? `
          <!-- Shipping Address -->
          <div class="section">
            <div class="section-title">🚚 כתובת משלוח</div>
            <div class="info-row">
              <span class="label">כתובת:</span>
              <span class="value">${order.shippingAddress.address_line1 || '-'}</span>
            </div>
            ${order.shippingAddress.address_line2 ? `
              <div class="info-row">
                <span class="label">כתובת נוספת:</span>
                <span class="value">${order.shippingAddress.address_line2}</span>
              </div>
            ` : ''}
            <div class="info-row">
              <span class="label">עיר:</span>
              <span class="value">${order.shippingAddress.city || '-'}</span>
            </div>
            <div class="info-row">
              <span class="label">מיקוד:</span>
              <span class="value">${order.shippingAddress.postal_code || '-'}</span>
            </div>
          </div>
        ` : (order.company?.address || order.company?.city) ? `
          <!-- Company Address as Shipping -->
          <div class="section">
            <div class="section-title">🚚 כתובת משלוח</div>
            ${order.company.address ? `
              <div class="info-row">
                <span class="label">כתובת:</span>
                <span class="value">${order.company.address}</span>
              </div>
            ` : ''}
            ${order.company.city ? `
              <div class="info-row">
                <span class="label">עיר:</span>
                <span class="value">${order.company.city}</span>
              </div>
            ` : ''}
            ${order.company.postal_code ? `
              <div class="info-row">
                <span class="label">מיקוד:</span>
                <span class="value">${order.company.postal_code}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <!-- Order Items -->
        <div class="section">
          <div class="section-title">🛍️ פריטי הזמנה</div>
          <table>
            <thead>
              <tr>
                <th style="width: 50px;">#</th>
                <th style="width: 120px;">מק״ט</th>
                <th>שם המוצר</th>
                <th style="width: 100px; text-align: center;">כמות</th>
              </tr>
            </thead>
            <tbody>
              ${sortOrderItems(order.items || []).map((item, index) => {
                const isTester = item.notes && item.notes.toLowerCase().includes('טסטר');
                const isBonus = item.notes && item.notes.toLowerCase().includes('בונוס');
                return `
                <tr${isTester || isBonus ? ' style="background: #fef9e7;"' : ''}>
                  <td style="text-align: center;">${index + 1}</td>
                  <td>
                    <code style="background: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 13px;">
                      ${item.product?.sku || item.product_snapshot?.sku || '-'}
                    </code>
                  </td>
                  <td>
                    <strong>${item.product?.name || item.product_snapshot?.name || 'מוצר לא ידוע'}</strong>
                    ${isTester ? '<span style="background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px; font-size: 11px; margin-right: 8px; font-weight: 600;">טסטר</span>' : ''}
                    ${isBonus ? '<span style="background: #e0e7ff; color: #3730a3; padding: 2px 6px; border-radius: 4px; font-size: 11px; margin-right: 8px; font-weight: 600;">בונוס</span>' : ''}
                    ${item.product?.brand || item.product_snapshot?.brand ? `<br><small style="color: #6b7280;">מותג: ${item.product?.brand || item.product_snapshot?.brand}</small>` : ''}
                  </td>
                  <td style="text-align: center; font-weight: 700; font-size: 16px;">${item.quantity || 0}</td>
                </tr>
              `}).join('') || '<tr><td colspan="4">אין פריטים</td></tr>'}
              <tr class="total-row">
                <td colspan="3" style="text-align: left; font-weight: 700;">סה״כ פריטים:</td>
                <td style="text-align: center; font-weight: 700; font-size: 18px;">
                  ${order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payment & Delivery Info -->
        <div class="section">
          <div class="section-title">📋 מידע נוסף</div>
          <div class="info-row">
            <span class="label">סטטוס הזמנה:</span>
            <span class="value">${getStatusText(order.status)}</span>
          </div>
          <div class="info-row">
            <span class="label">אמצעי תשלום:</span>
            <span class="value">${getPaymentMethodText(order.payment_method)}</span>
          </div>
          <div class="info-row">
            <span class="label">סטטוס תשלום:</span>
            <span class="value">${getPaymentStatusText(order.payment_status)}</span>
          </div>
          ${order.tracking_number ? `
            <div class="info-row">
              <span class="label">מספר מעקב:</span>
              <span class="value">${order.tracking_number}</span>
            </div>
          ` : ''}
          ${order.notes ? `
            <div class="info-row">
              <span class="label">הערות:</span>
              <span class="value">${order.notes}</span>
            </div>
          ` : ''}
        </div>

        <div class="footer">
          <p>תודה על ההזמנה! 🙏</p>
          <p>הודפס בתאריך: ${new Date().toLocaleDateString('he-IL')} ${new Date().toLocaleTimeString('he-IL')}</p>
        </div>
      </body>
      </html>
    `;

    function getStatusText(status) {
      const statusMap = {
        'pending': 'ממתין לטיפול',
        'processing': 'בטיפול',
        'confirmed': 'אושר',
        'shipped': 'נשלח',
        'delivered': 'נמסר',
        'cancelled': 'בוטל'
      };
      return statusMap[status] || status;
    }

    function getPaymentMethodText(method) {
      const methodMap = {
        'credit_card': 'כרטיס אשראי',
        'bank_transfer': 'העברה בנקאית',
        'cash': 'מזומן',
        'check': 'צ\'ק',
        'credit': 'אשראי עסקי'
      };
      return methodMap[method] || method;
    }

    function getPaymentStatusText(status) {
      const statusMap = {
        'pending': 'ממתין לתשלום',
        'paid': 'שולם',
        'partially_paid': 'שולם חלקית',
        'refunded': 'הוחזר',
        'failed': 'נכשל'
      };
      return statusMap[status] || status;
    }

    // Open print window
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        alert('נא לאפשר חלונות קופצים (popups) בדפדפן כדי להדפיס');
        return;
      }
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error('Print error:', error);
      alert('שגיאה בהדפסה: ' + error.message);
    }
  };

  // Order data state
  const [orderData, setOrderData] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [editData, setEditData] = useState(null);
  const [editedItems, setEditedItems] = useState([]);
  const [editOrderDiscount, setEditOrderDiscount] = useState(0);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Helper function to get payment method labels
  const getPaymentMethodLabel = (method) => {
    const methods = {
      'cash_on_delivery': 'מזומן בזמן האספקה',
      'credit_card': 'כרטיס אשראי',
      'bank_transfer': 'העברה בנקאית',
      'check': 'צ\'ק',
      'company_credit': 'אשראי חברה',
      'installments': 'תשלומים',
      'wire_transfer': 'העברה בינלאומית',
      'cash': 'מזומן'
    };
    return methods[method] || method;
  };

  // Load order data when modal opens
  const loadOrderData = async () => {
    try {
      setLoading(true);
      // Try to fetch full order details from API
      const response = await axios.get(`/api/orders/${order.id}`).catch((error) => {
        // Fallback to the order data we already have
        return { data: order };
      });

      // Extract order data (backend returns { order: {...} })
      const orderData = response.data.order || response.data;

      if (!orderData) {
        setOrderData(order);
        return;
      }

      // Order-level discount only. Item-level discounts are already baked into
      // unit_price (unit_price is the final discounted price), so item
      // discount_amount must NOT be subtracted again from the subtotal.
      const orderDiscount = Math.abs(parseFloat(orderData.discount_amount || 0));

      // Calculate subtotal from items, excluding tester/bonus items (they are always 0 price)
      const calculatedSubtotal = (orderData.items || []).reduce((sum, item) => {
        // Check if item is tester or bonus - these should always be 0
        const isTesterOrBonus = item.is_tester || item.is_bonus || 
          (item.notes && (item.notes.includes('טסטר') || item.notes.includes('בונוס')));
        if (isTesterOrBonus) {
          return sum;
        }
        const price = parseFloat(item.unit_price || item.price || 0);
        return sum + (price * (item.quantity || 1));
      }, 0);

      // Transform the data to match what the component expects
      // Build customer name: prefer user name, fall back to company name
      const userName = orderData.user 
        ? `${orderData.user.first_name || ''} ${orderData.user.last_name || ''}`.trim() 
        : '';
      const companyName = orderData.company?.name || '';
      const customerName = userName || companyName || 'לקוח לא ידוע';

      const transformedData = {
        ...orderData,
        paid_amount: parseFloat(orderData.paid_amount || 0),
        remaining_amount: parseFloat(orderData.total_amount || 0) - parseFloat(orderData.paid_amount || 0),
        customer: {
          id: orderData.user?.id,
          name: customerName,
          email: orderData.user?.email || orderData.company?.email || 'לא זמין',
          phone: orderData.user?.phone || orderData.company?.phone || 'לא זמין',
          company: companyName || null,
          company_address: orderData.company?.address || null,
          company_city: orderData.company?.city || null,
          company_postal_code: orderData.company?.postal_code || null,
          type: orderData.order_type || 'b2c'
        },
        totals: {
          // Prices are BEFORE VAT - VAT is added at the end.
          // Matches server calculation (routes/orders.js): VAT is computed on
          // the subtotal AFTER the order-level discount.
          subtotal: calculatedSubtotal,
          discount: orderDiscount,
          tax: Math.max(0, calculatedSubtotal - orderDiscount) * 0.18,
          shipping: parseFloat(orderData.shipping_amount || 0),
          total: Math.max(0, calculatedSubtotal - orderDiscount) * 1.18 + parseFloat(orderData.shipping_amount || 0)
        },
        payment: {
          method: orderData.payment_method,
          status: orderData.payment_status,
          card_last4: null // We don't store card details
        },
        shipping: {
          method: orderData.shipping_method || 'משלוח רגיל',
          cost: parseFloat(orderData.shipping_amount || 0),
          tracking_number: orderData.tracking_number
        },
        timeline: [
          {
            status: orderData.status,
            timestamp: orderData.createdAt,
            note: `הזמנה נוצרה`
          }
        ]
      };

      setOrderData(transformedData);
      setOrderItems(sortOrderItems(orderData.items || []));

    } catch (error) {
      console.error('Error loading order data:', error);
      setOrderData(order);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && order) {
      loadOrderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order]);

  // Load products when add product modal opens
  useEffect(() => {
    if (showAddProductModal) {
      loadAvailableProducts(productSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddProductModal]);

  const handleStatusUpdate = async (newStatus) => {
    try {
      const response = await axios.put(`/api/orders/${order.id}/status`, {
        status: newStatus,
        internal_notes: `סטטוס עודכן ל: ${getStatusLabel(newStatus)}`
      });

      // Update local state
      setOrderData(prev => ({
        ...prev,
        status: newStatus,
        timeline: [
          ...prev.timeline,
          {
            status: newStatus,
            timestamp: new Date().toISOString(),
            note: `סטטוס עודכן ל: ${getStatusLabel(newStatus)}`,
            user: 'מנהל'
          }
        ]
      }));

      // Notify parent component
      if (onOrderUpdated) {
        onOrderUpdated({ ...order, status: newStatus });
      }

      toast.success('סטטוס ההזמנה עודכן בהצלחה');

    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בעדכון סטטוס ההזמנה');
    }
  };

  const handlePaymentStatusUpdate = async (newPaymentStatus) => {
    try {
      const response = await axios.put(`/api/orders/${order.id}/payment`, {
        payment_status: newPaymentStatus
      });

      // Update local state
      setOrderData(prev => ({
        ...prev,
        payment_status: newPaymentStatus,
        payment: {
          ...prev.payment,
          status: newPaymentStatus
        }
      }));

      // Notify parent component
      if (onOrderUpdated) {
        onOrderUpdated({ ...order, payment_status: newPaymentStatus });
      }

      toast.success('סטטוס התשלום עודכן בהצלחה');

    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בעדכון סטטוס התשלום');
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'בהמתנה',
      processing: 'בעיבוד',
      shipped: 'נשלח',
      delivered: 'נמסר',
      cancelled: 'בוטל',
      refunded: 'הוחזר'
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: 'בהמתנה לתשלום',
      paid: 'שולם',
      failed: 'נכשל',
      refunded: 'הוחזר',
      partially_refunded: 'הוחזר חלקית'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      refunded: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load available products for adding to order
  const loadAvailableProducts = async (searchTerm = '') => {
    try {
      setLoadingProducts(true);
      
      const params = {
        page: 1,
        limit: 200,
        status: 'active'
      };
      
      // Add search parameter if provided
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await axios.get('/api/products', { params });
      const products = response.data.products || [];
      if (products.length > 0) {
      }
      setAvailableProducts(products);
    } catch (error) {
      toast.error('שגיאה בטעינת מוצרים');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle search with debounce
  const handleProductSearch = (value) => {
    setProductSearchTerm(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      loadAvailableProducts(value);
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
  };

  // Add new product to order
  const handleAddProduct = (productId, options = {}) => {
    // options: { is_tester: boolean, is_bonus: boolean }
    const productIdToUse = productId || selectedProductId;
    
    if (!productIdToUse) {
      toast.error('אנא בחר מוצר');
      return;
    }

    const product = availableProducts.find(p => p.id === productIdToUse);
    if (!product) {
      toast.error('מוצר לא נמצא');
      return;
    }

    const isTester = options.is_tester || false;
    const isBonus = options.is_bonus || false;

    // Check if product already exists in order (same type)
    // For regular items: check if exists without tester/bonus notes
    // For tester: check if exists with tester note
    // For bonus: check if exists with bonus note
    const existingItem = editedItems.find(item => {
      if (item.product_id !== product.id || item.removed) {
        return false;
      }
      
      const itemNotes = item.notes || '';
      const itemIsTester = itemNotes.includes('טסטר');
      const itemIsBonus = itemNotes.includes('בונוס');
      
      // Match exact type
      if (isTester) {
        return itemIsTester;
      } else if (isBonus) {
        return itemIsBonus;
      } else {
        // Regular item - should not have tester or bonus notes
        return !itemIsTester && !itemIsBonus;
      }
    });
    
    if (existingItem) {
      const itemType = isBonus ? 'בונוס' : isTester ? 'טסטר' : 'רגיל';
      toast.error(`המוצר כבר קיים בהזמנה (${itemType})`);
      return;
    }

    // Determine price based on order type and tester/bonus
    let price = orderData.customer.type === 'b2b' 
      ? (product.price_b2b || product.price_b2c || 0)
      : (product.price_b2c || product.price_b2b || 0);
    
    // If tester or bonus, price is always 0
    if (isTester || isBonus) {
      price = 0;
    }

    // Build notes
    let notes = '';
    if (isTester) {
      notes = 'טסטר - לספק בעלות 0';
    }
    if (isBonus) {
      notes = notes ? `${notes}\nבונוס - מתנה` : 'בונוס - מתנה';
    }

    // Add new item (without id - will be created on backend)
    const newItem = {
      id: `new-${Date.now()}-${Math.random()}`, // Temporary ID for React key
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      product_image: product.images?.[0] || '/placeholder-product.png',
      quantity: 1,
      unit_price: price,
      discount_percent: 0,
      removed: false,
      isNew: true, // Flag to identify new items
      notes: notes || undefined
    };

    setEditedItems(prev => [...prev, newItem]);
    setSelectedProductId('');
    setProductSearchTerm('');
    
    const itemType = isBonus ? 'בונוס' : isTester ? 'טסטר' : '';
    toast.success(`המוצר ${itemType ? `(${itemType}) ` : ''}נוסף להזמנה`);
    
    // Don't close modal automatically - let user add more products
    // setShowAddProductModal(false);
  };

  const handleEditOrder = async () => {
    // Initialize edit data with current order data
    setEditData({
      notes: orderData.notes || '',
      shipping_address: {
        first_name: orderData.shippingAddress?.first_name || orderData.user?.first_name || '',
        last_name: orderData.shippingAddress?.last_name || orderData.user?.last_name || '',
        address_line_1: orderData.shippingAddress?.address_line_1 || '',
        city: orderData.shippingAddress?.city || '',
        postal_code: orderData.shippingAddress?.postal_code || '',
        phone: orderData.shippingAddress?.phone || orderData.user?.phone || '',
        email: orderData.shippingAddress?.email || orderData.user?.email || ''
      }
    });
    // Initialize order-level discount
    setEditOrderDiscount(parseFloat(orderData.discount_amount || 0));

    // Initialize edited items with current order items
    setEditedItems(orderItems.map(item => {
      const product = item.product || item.product_snapshot || {};
      const currentUnitPrice = parseFloat(item.unit_price);
      // Stored unit_price is the FINAL (discounted) price. Reconstruct the base
      // price primarily from the stored informational discount_amount, falling
      // back to the agent's product_snapshot.original_price. Without this,
      // re-applying a discount in edit mode compounds it on the discounted price.
      const quantity = parseInt(item.quantity) || 1;
      const perUnitDiscount = (parseFloat(item.discount_amount) || 0) / quantity;
      const originalPrice = perUnitDiscount > 0
        ? currentUnitPrice + perUnitDiscount
        : parseFloat(item.product_snapshot?.original_price || item.unit_price);
      const discountPercent = originalPrice > 0 && originalPrice > currentUnitPrice
        ? ((originalPrice - currentUnitPrice) / originalPrice) * 100
        : 0;
      return {
        id: item.id,
        product_id: item.product_id,
        product_name: product.name || item.product_name || 'מוצר',
        product_sku: product.sku || item.product_sku || 'N/A',
        product_image: product.images?.[0] || item.product_image || '/placeholder-product.png',
        quantity: item.quantity,
        original_price: originalPrice,
        unit_price: originalPrice, // base price before discount (agent's original_price)
        discount_percent: parseFloat(discountPercent.toFixed(2)),
        removed: false,
        notes: item.notes || undefined // Preserve existing notes (for tester/bonus items)
      };
    }));
    
    // Load available products for adding
    await loadAvailableProducts();
    
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData(null);
    setEditedItems([]);
    setEditOrderDiscount(0);
  };

  // Update item quantity
  const handleItemQuantityChange = (itemId, quantity) => {
    const qty = Math.max(1, parseInt(quantity) || 1); // Minimum 1
    setEditedItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, quantity: qty } : item
    ));
  };

  // Update item price
  const handleItemPriceChange = (itemId, price) => {
    setEditedItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, unit_price: parseFloat(price) || 0 } : item
    ));
  };

  // Update item discount
  const handleItemDiscountChange = (itemId, discount) => {
    setEditedItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, discount_percent: parseFloat(discount) || 0 } : item
    ));
  };

  // Remove item
  const handleRemoveItem = (itemId) => {
    setEditedItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, removed: true } : item
    ));
  };

  // Restore removed item
  const handleRestoreItem = (itemId) => {
    setEditedItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, removed: false } : item
    ));
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);

      // Prepare updated items (excluding removed ones and items with quantity 0)
      const updatedItems = editedItems
        .filter(item => !item.removed && item.quantity > 0)
        .map(item => ({
          // Only include id for existing items (not new ones)
          ...(item.id && !item.isNew ? { id: item.id } : {}),
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent,
          // Calculate final price after discount
          final_price: item.unit_price * (1 - item.discount_percent / 100),
          // Include notes if present (for tester/bonus items)
          notes: item.notes || undefined
        }));

      // Update order with edit data
      const response = await axios.put(`/api/orders/${order.id}`, {
        notes: editData.notes,
        shipping_address: editData.shipping_address,
        items: updatedItems,
        discount_amount: editOrderDiscount
      });

      // Update discount_amount from the PUT response immediately so the view
      // shows the correct value even before the full reload completes.
      const updatedOrder = response.data.order;
      setOrderData(prev => ({
        ...prev,
        notes: editData.notes,
        shippingAddress: editData.shipping_address,
        discount_amount: updatedOrder?.discount_amount ?? editOrderDiscount,
        total_amount: updatedOrder?.total_amount ?? prev.total_amount,
        totals: prev.totals ? {
          ...prev.totals,
          discount: Math.abs(parseFloat(updatedOrder?.discount_amount ?? editOrderDiscount)),
          total: parseFloat(updatedOrder?.total_amount ?? prev.totals.total),
          tax: parseFloat(updatedOrder?.tax_amount ?? prev.totals.tax),
          subtotal: parseFloat(updatedOrder?.subtotal ?? prev.totals.subtotal),
        } : prev.totals
      }));

      // Exit edit mode BEFORE reloading so the view shows updated data
      setIsEditMode(false);
      setEditData(null);
      setEditedItems([]);

      // Reload order data to get fully refreshed items list
      await loadOrderData();

      if (onOrderUpdated) {
        onOrderUpdated(response.data.order);
      }

      toast.success('פרטי ההזמנה עודכנו בהצלחה');

    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בעדכון פרטי ההזמנה');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDataChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Show modal even if loading or no data
  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center justify-between w-full">
          <span>{isEditMode ? 'עריכת הזמנה' : 'הזמנה'} {orderData?.order_number || '...'}</span>
          {isEditMode ? (
            <div className="flex items-center gap-2">
              <AccessibleButton
                onClick={handleSaveEdit}
                disabled={loading}
                loading={loading}
                variant="primary"
                size="md"
                icon={CheckCircleIcon}
                iconPosition="left"
                ariaLabel="שמור שינויים"
              >
                שמור
              </AccessibleButton>
              <AccessibleButton
                onClick={handleCancelEdit}
                disabled={loading}
                variant="ghost"
                size="md"
                icon={XCircleIcon}
                iconPosition="left"
                ariaLabel="ביטול עריכה"
              >
                ביטול
              </AccessibleButton>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <AccessibleButton
                onClick={handlePrintOrder}
                variant="outline"
                size="md"
                icon={PrinterIcon}
                iconPosition="left"
                ariaLabel="הדפס הזמנה"
              >
                הדפס
              </AccessibleButton>
              <AccessibleButton
                onClick={handleEditOrder}
                variant="primary"
                size="md"
                icon={PencilIcon}
                iconPosition="left"
                ariaLabel="ערוך הזמנה"
              >
                ערוך
              </AccessibleButton>
            </div>
          )}
        </div>
      }
      size="full"
      preventClose={loading}
    >
      {/* Loading State */}
      {loading && !orderData ? (
        <div className="flex flex-col items-center justify-center py-12 min-h-[400px]">
          <LoadingSpinner size="xl" color="primary" text="טוען פרטי הזמנה..." />
        </div>
      ) : !orderData ? (
        <div className="flex flex-col items-center justify-center py-12 min-h-[400px]">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mb-4" />
          <p className="text-gray-900 font-bold text-lg mb-2">שגיאה בטעינת פרטי ההזמנה</p>
          <p className="text-gray-600 mb-4">אנא נסה שנית מאוחר יותר</p>
          <AccessibleButton
            onClick={onClose}
            variant="outline"
            size="md"
            ariaLabel="סגור חלון"
          >
            סגור
          </AccessibleButton>
        </div>
      ) : (
        <React.Fragment>
          {/* Dedicated Print Layout */}
          <div className="screen-only print-only">
            <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '2px solid #000', paddingBottom: '15px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '10px' }}>הזמנה #{orderData.order_number}</h1>
              <p style={{ fontSize: '14px', color: '#666' }}>תאריך: {formatDate(orderData.createdAt)}</p>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '15px', borderBottom: '1px solid #ccc', paddingBottom: '8px' }}>פרטי לקוח</h2>
              <table style={{ width: '100%', marginBottom: '10px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '8px', fontWeight: 'bold', width: '25%' }}>שם:</td>
                    <td style={{ padding: '8px' }}>{orderData.customer.name}</td>
                    <td style={{ padding: '8px', fontWeight: 'bold', width: '25%' }}>טלפון:</td>
                    <td style={{ padding: '8px' }}>{orderData.customer.phone}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>אימייל:</td>
                    <td style={{ padding: '8px' }}>{orderData.customer.email}</td>
                    {orderData.shippingAddress && (
                      <>
                        <td style={{ padding: '8px', fontWeight: 'bold' }}>כתובת למשלוח:</td>
                        <td style={{ padding: '8px' }}>
                          {orderData.shippingAddress.street || orderData.shippingAddress.address_line_1}, {orderData.shippingAddress.city}
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '15px', borderBottom: '1px solid #ccc', paddingBottom: '8px' }}>פריטים בהזמנה</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>מוצר</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>מק"ט</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>כמות</th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.product_name || item.product?.name}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.product_sku || item.product?.sku || 'N/A'}</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', fontWeight: 'bold' }}>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="printable-order-details no-print">

            {/* Compact Order Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                {/* Order Info */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusColor(orderData.status)}`}>
                    {getStatusLabel(orderData.status)}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    orderData.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                    orderData.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {orderData.payment_status === 'paid' ? '✓ שולם' : 
                     orderData.payment_status === 'pending' ? '◷ ממתין' : '✗ לא שולם'}
                  </span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs text-gray-500">{formatDate(orderData.createdAt)}</span>
                </div>
                
                {/* Total */}
                <div className="bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                  <span className="font-bold text-gray-900">{formatCurrency(orderData.totals.total)}</span>
                </div>
              </div>
              
              {/* Fulfillment Progress Bar - Centered with Arrows */}
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-center max-w-2xl mx-auto">
                  {[
                    { key: 'pending', label: 'התקבלה', icon: '📥' },
                    { key: 'processing', label: 'בטיפול', icon: '⚙️' },
                    { key: 'shipped', label: 'נשלחה', icon: '🚚' },
                    { key: 'delivered', label: 'נמסרה', icon: '✅' }
                  ].map((step, idx, arr) => {
                    const statusOrder = ['pending', 'processing', 'shipped', 'delivered'];
                    const currentIdx = statusOrder.indexOf(orderData.status);
                    const stepIdx = statusOrder.indexOf(step.key);
                    const isActive = stepIdx <= currentIdx && orderData.status !== 'cancelled';
                    const isCurrent = step.key === orderData.status;
                    
                    return (
                      <React.Fragment key={step.key}>
                        {/* Step Button */}
                        <div className="flex items-center flex-1">
                          <button
                            onClick={() => orderData.status !== 'cancelled' && handleStatusUpdate(step.key)}
                            disabled={orderData.status === 'cancelled'}
                            className={`flex flex-col items-center group w-full ${orderData.status === 'cancelled' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                              isCurrent 
                                ? 'bg-primary-600 text-white shadow-lg ring-4 ring-primary-100' 
                                : isActive 
                                ? 'bg-green-500 text-white' 
                                : 'bg-gray-200 text-gray-400 group-hover:bg-gray-300'
                            }`}>
                              {isActive && !isCurrent ? '✓' : step.icon}
                            </div>
                            <span className={`text-xs mt-1.5 font-medium ${
                              isCurrent ? 'text-primary-600' : isActive ? 'text-green-600' : 'text-gray-400'
                            }`}>
                              {step.label}
                            </span>
                          </button>
                        </div>
                        
                        {/* Arrow between steps */}
                        {idx < arr.length - 1 && (
                          <div className="flex items-center justify-center mx-2 flex-shrink-0">
                            <div className="flex items-center">
                              <div className={`h-0.5 w-6 ${
                                stepIdx < currentIdx ? 'bg-green-500' : 'bg-gray-200'
                              }`}></div>
                              <svg 
                                className={`w-5 h-5 ${
                                  stepIdx < currentIdx ? 'text-green-500' : 'text-gray-300'
                                }`}
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                                style={{ transform: 'scaleX(-1)' }}
                              >
                                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                {orderData.status === 'cancelled' && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-center">
                    <span className="text-sm text-red-700 font-medium">❌ הזמנה בוטלה</span>
                  </div>
                )}
              </div>
            </div>

            {/* Shopify-Style Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              
              {/* Main Content - Left Side (Products & Summary) */}
              <div className="flex-1 space-y-4">

                {/* Products Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">
                      פריטים ({isEditMode ? editedItems.filter(i => !i.removed).length : orderItems.length})
                    </h3>
                    {isEditMode && (
                      <button
                        onClick={() => {
                          setShowAddProductModal(true);
                        }}
                        className="px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
                      >
                        <span>+</span>
                        הוסף מוצר
                      </button>
                    )}
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">מוצר</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">מחיר</th>
                          {isEditMode && (
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">הנחה %</th>
                          )}
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">כמות</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">סה"כ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {isEditMode ? (
                          editedItems.map((item) => {
                            const finalPrice = item.unit_price * (1 - item.discount_percent / 100) * item.quantity;
                            return (
                              <tr key={item.id} className={`border-b border-gray-100 ${item.removed ? 'bg-red-50 opacity-60' : 'hover:bg-gray-50'}`}>
                                <td className="px-4 py-4">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={item.product_image || '/placeholder-product.png'}
                                      alt={item.product_name}
                                      className="w-14 h-14 object-cover rounded-lg border border-gray-200 shadow-sm"
                                      onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                                    />
                                    <div className="min-w-0">
                                      <p className="font-semibold text-gray-900 text-sm">{item.product_name}</p>
                                      <p className="text-xs text-gray-500 font-mono mt-1">{item.product_sku}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  {!item.removed ? (
                                    <div className="flex flex-col items-center gap-0.5">
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.unit_price}
                                        onChange={(e) => handleItemPriceChange(item.id, e.target.value)}
                                        className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm text-center focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                      />
                                      <span className="text-xs text-gray-400">מחיר בסיס</span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  {!item.removed ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                      value={item.discount_percent}
                                      onChange={(e) => handleItemDiscountChange(item.id, e.target.value)}
                                      className="w-16 px-2 py-1.5 border border-gray-300 rounded-md text-center text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  {!item.removed ? (
                                    <input
                                      type="number"
                                      min="1"
                                      value={item.quantity}
                                      onChange={(e) => handleItemQuantityChange(item.id, e.target.value)}
                                      className="w-16 px-2 py-1.5 border border-gray-300 rounded-md text-center text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                  ) : (
                                    <span className="text-red-500 text-xs font-medium">יוסר</span>
                                  )}
                                </td>
                                <td className="px-4 py-4 text-left">
                                  {!item.removed ? (
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="font-bold text-gray-900">{formatCurrency(finalPrice)}</span>
                                      <button
                                        onClick={() => handleRemoveItem(item.id)}
                                        className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                      >
                                        הסר פריט
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleRestoreItem(item.id)}
                                      className="text-xs text-green-600 hover:text-green-800 font-medium hover:underline"
                                    >
                                      ↩ שחזר
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          orderItems.map((item) => {
                            const product = item.product || item.product_snapshot || {};
                            const productImage = product.images?.[0] || item.product_image || '/placeholder-product.png';
                            const productName = product.name || item.product_name || 'מוצר לא ידוע';
                            const productSku = product.sku || item.product_sku || '-';
                            const isTester = item.notes && item.notes.includes('טסטר');
                            const isBonus = item.notes && item.notes.includes('בונוס');
                            const isFreeItem = isTester || isBonus;
                            const unitPrice = parseFloat(item.unit_price) || 0;
                            const originalPrice = parseFloat(item.product_snapshot?.original_price || item.unit_price) || 0;
                            const hasItemDiscount = !isFreeItem && originalPrice > unitPrice + 0.01;
                            const itemDiscountPct = hasItemDiscount
                              ? Math.round(((originalPrice - unitPrice) / originalPrice) * 100)
                              : 0;

                            return (
                              <tr key={item.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isFreeItem ? 'bg-amber-50/30' : ''}`}>
                                <td className="px-4 py-4">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={productImage}
                                      alt={productName}
                                      className="w-14 h-14 object-cover rounded-lg border border-gray-200 shadow-sm"
                                      onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-product.png'; }}
                                    />
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-semibold text-gray-900 text-sm">{productName}</p>
                                        {isTester && (
                                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200">
                                            🧪 טסטר
                                          </span>
                                        )}
                                        {isBonus && (
                                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded-full border border-purple-200">
                                            🎁 בונוס
                                          </span>
                                        )}
                                        {hasItemDiscount && (
                                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
                                            -{itemDiscountPct}%
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1 font-mono">{productSku}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  {isFreeItem ? (
                                    <span className="text-gray-400 text-sm">-</span>
                                  ) : hasItemDiscount ? (
                                    <div className="flex flex-col items-center gap-0.5">
                                      <span className="text-xs text-gray-400 line-through">{formatCurrency(originalPrice)}</span>
                                      <span className="text-sm font-semibold text-red-600">{formatCurrency(unitPrice)}</span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-700">{formatCurrency(unitPrice)}</span>
                                  )}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <span className="inline-flex items-center justify-center min-w-[2rem] h-8 bg-gray-100 rounded-full font-bold text-gray-900 text-sm px-2">
                                    {item.quantity}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  {isFreeItem ? (
                                    <span className="text-green-600 font-semibold text-sm">חינם</span>
                                  ) : (
                                    <span className="font-bold text-gray-900">
                                      {formatCurrency(Math.abs(item.total_price || unitPrice * item.quantity))}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Order Summary */}
                  <div className="px-4 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="max-w-xs mr-auto space-y-2">
                      {isEditMode ? (() => {
                        // Live totals calculation in edit mode
                        const liveSubtotal = editedItems.filter(i => !i.removed).reduce((sum, i) => {
                          const isFree = i.notes && (i.notes.includes('טסטר') || i.notes.includes('בונוס'));
                          if (isFree) return sum;
                          return sum + i.unit_price * (1 - i.discount_percent / 100) * i.quantity;
                        }, 0);
                        const subtotalAfterDiscount = Math.max(0, liveSubtotal - editOrderDiscount);
                        const liveTax = subtotalAfterDiscount * 0.18;
                        const liveShipping = orderData.totals.shipping;
                        const liveTotal = subtotalAfterDiscount + liveTax + liveShipping;

                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">סכום ביניים</span>
                              <span className="text-gray-900">{formatCurrency(liveSubtotal)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-green-600">הנחה כללית</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editOrderDiscount}
                                onChange={(e) => setEditOrderDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                                className="w-24 px-2 py-1 border border-green-300 rounded-md text-sm text-center focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">משלוח</span>
                              <span className="text-gray-900">{formatCurrency(liveShipping)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">מע"מ</span>
                              <span className="text-gray-900">{formatCurrency(liveTax)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-gray-300">
                              <span className="font-bold text-gray-900">סה"כ</span>
                              <span className="font-bold text-lg text-primary-700">{formatCurrency(liveTotal)}</span>
                            </div>
                          </>
                        );
                      })() : (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">סכום ביניים</span>
                            <span className="text-gray-900">{formatCurrency(orderData.totals.subtotal)}</span>
                          </div>
                          {parseFloat(orderData.totals?.discount || 0) > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-green-600">הנחה</span>
                              <span className="text-green-600">-{formatCurrency(orderData.totals.discount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">משלוח</span>
                            <span className="text-gray-900">{formatCurrency(orderData.totals.shipping)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">מע"מ</span>
                            <span className="text-gray-900">{formatCurrency(orderData.totals.tax)}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-300">
                            <span className="font-bold text-gray-900">סה"כ</span>
                            <span className="font-bold text-lg text-primary-700">{formatCurrency(orderData.totals.total)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Agent Notes - if exists */}
                {orderData.seller && (
                  <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <UserIcon className="h-4 w-4 text-purple-600" />
                      <span className="font-semibold text-purple-900 text-sm">פעולות סוכן שטח</span>
                    </div>
                    <p className="text-sm text-purple-800">
                      סוכן: {orderData.seller.first_name} {orderData.seller.last_name}
                    </p>
                    {orderData.seller.email && (
                      <p className="text-xs text-purple-600 mt-1">{orderData.seller.email}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar - Right Side (Customer & Details) */}
              <div className="lg:w-80 space-y-4">
                
                {/* Customer Card - Shopify Style */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      {orderData.customer.type === 'b2b' ? (
                        <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                      ) : (
                        <UserIcon className="h-4 w-4 text-green-600" />
                      )}
                      לקוח
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      orderData.customer.type === 'b2b' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {orderData.customer.type === 'b2b' ? 'עסקי' : 'פרטי'}
                    </span>
                  </div>
                  <div className="p-4">
                    {/* Customer Name with Avatar */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        orderData.customer.type === 'b2b' ? 'bg-blue-500' : 'bg-green-500'
                      }`}>
                        {orderData.customer.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{orderData.customer.name}</p>
                        {orderData.customer.company && (
                          <p className="text-xs text-gray-500">{orderData.customer.company}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Contact Info */}
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">פרטי התקשרות</p>
                      {orderData.customer.email && orderData.customer.email !== 'לא זמין' ? (
                        <a href={`mailto:${orderData.customer.email}`} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline">
                          <EnvelopeIcon className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{orderData.customer.email}</span>
                        </a>
                      ) : (
                        <p className="flex items-center gap-2 text-sm text-gray-400">
                          <EnvelopeIcon className="h-4 w-4 flex-shrink-0" />
                          <span>לא זמין</span>
                        </p>
                      )}
                      {orderData.customer.phone && orderData.customer.phone !== 'לא זמין' ? (
                        <a href={`tel:${orderData.customer.phone}`} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline">
                          <PhoneIcon className="h-4 w-4 flex-shrink-0" />
                          <span>{orderData.customer.phone}</span>
                        </a>
                      ) : (
                        <p className="flex items-center gap-2 text-sm text-gray-400">
                          <PhoneIcon className="h-4 w-4 flex-shrink-0" />
                          <span>לא זמין</span>
                        </p>
                      )}
                    </div>

                    {/* Company Address */}
                    {orderData.customer.company && (
                      <div className="border-t border-gray-100 pt-3 mt-3 space-y-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">כתובת חברה</p>
                        {orderData.customer.company_address || orderData.customer.company_city ? (
                          <>
                            {orderData.customer.company_address && (
                              <p className="flex items-center gap-2 text-sm text-gray-700">
                                <MapPinIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                <span>{orderData.customer.company_address}</span>
                              </p>
                            )}
                            {orderData.customer.company_city && (
                              <p className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="w-4 flex-shrink-0" />
                                <span>{orderData.customer.company_city}{orderData.customer.company_postal_code ? `, ${orderData.customer.company_postal_code}` : ''}</span>
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="flex items-center gap-2 text-sm text-gray-400 italic">
                            <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                            <span>כתובת לא הוגדרה</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Shipping Address Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <MapPinIcon className="h-4 w-4 text-purple-600" />
                      כתובת משלוח
                    </h3>
                    {!isEditMode && (
                      <button onClick={handleEditOrder} className="text-xs text-primary-600 hover:text-primary-700">
                        ערוך
                      </button>
                    )}
                  </div>
                  <div className="p-4">
                    {isEditMode ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder="כתובת"
                          value={editData?.shipping_address?.address_line_1 || ''}
                          onChange={(e) => handleEditDataChange('shipping_address.address_line_1', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="עיר"
                            value={editData?.shipping_address?.city || ''}
                            onChange={(e) => handleEditDataChange('shipping_address.city', e.target.value)}
                            className="px-3 py-2 border rounded-md text-sm"
                          />
                          <input
                            type="text"
                            placeholder="מיקוד"
                            value={editData?.shipping_address?.postal_code || ''}
                            onChange={(e) => handleEditDataChange('shipping_address.postal_code', e.target.value)}
                            className="px-3 py-2 border rounded-md text-sm"
                          />
                        </div>
                        <input
                          type="tel"
                          placeholder="טלפון"
                          value={editData?.shipping_address?.phone || ''}
                          onChange={(e) => handleEditDataChange('shipping_address.phone', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md text-sm"
                        />
                      </div>
                    ) : orderData.shippingAddress ? (
                      <div className="text-sm text-gray-700 space-y-1">
                        <p className="font-medium text-gray-900">
                          {orderData.shippingAddress.recipient_name || 
                           `${orderData.shippingAddress.first_name || ''} ${orderData.shippingAddress.last_name || ''}`.trim()}
                        </p>
                        <p>{orderData.shippingAddress.street || orderData.shippingAddress.address_line_1}</p>
                        <p>{orderData.shippingAddress.city} {orderData.shippingAddress.postal_code}</p>
                        {orderData.shippingAddress.phone && (
                          <p className="text-gray-500 pt-1">📞 {orderData.shippingAddress.phone}</p>
                        )}
                      </div>
                    ) : orderData.customer?.company_address || orderData.customer?.company_city || orderData.company?.address || orderData.company?.city ? (
                      <div className="text-sm text-gray-700 space-y-1">
                        {(orderData.customer?.company || orderData.company?.name) && (
                          <p className="font-medium text-gray-900">{orderData.customer?.company || orderData.company?.name}</p>
                        )}
                        {(orderData.customer?.company_address || orderData.company?.address) && (
                          <p>{orderData.customer?.company_address || orderData.company?.address}</p>
                        )}
                        {(orderData.customer?.company_city || orderData.company?.city) && (
                          <p>{orderData.customer?.company_city || orderData.company?.city}{(orderData.customer?.company_postal_code || orderData.company?.postal_code) ? ` ${orderData.customer?.company_postal_code || orderData.company?.postal_code}` : ''}</p>
                        )}
                        {(orderData.company?.phone && !orderData.shippingAddress) && (
                          <p className="text-gray-500 pt-1">📞 {orderData.company.phone}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">כתובת לא הוגדרה</p>
                    )}
                  </div>
                </div>

                {/* Payment Info Card - Shopify Style */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <CreditCardIcon className="h-4 w-4 text-green-600" />
                      תשלום
                    </h3>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      orderData.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                      orderData.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      orderData.payment_status === 'refunded' ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {orderData.payment_status === 'paid' ? '✓ שולם' : 
                       orderData.payment_status === 'pending' ? '◷ ממתין' : 
                       orderData.payment_status === 'refunded' ? '↩ הוחזר' : '✗ נכשל'}
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    {/* Payment Method */}
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">אמצעי תשלום</span>
                      <span className="text-sm font-medium text-gray-900">
                        {getPaymentMethodLabel(orderData.payment_method) || 'לא צוין'}
                      </span>
                    </div>
                    
                    {/* Amount Breakdown */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">סכום הזמנה</span>
                        <span className="text-gray-900">{formatCurrency(orderData.totals.total)}</span>
                      </div>
                      {orderData.paid_amount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">שולם</span>
                          <span className="text-green-600 font-medium">{formatCurrency(orderData.paid_amount)}</span>
                        </div>
                      )}
                      {(orderData.totals.total - (orderData.paid_amount || 0)) > 0 && orderData.payment_status !== 'paid' && (
                        <div className="flex justify-between pt-2 border-t border-gray-100">
                          <span className="text-gray-700 font-medium">יתרה לתשלום</span>
                          <span className="text-red-600 font-bold">
                            {formatCurrency(orderData.totals.total - (orderData.paid_amount || 0))}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Payment Status Buttons */}
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">עדכון מהיר:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {['pending', 'paid', 'failed', 'refunded'].map(pStatus => (
                          <button
                            key={pStatus}
                            onClick={() => handlePaymentStatusUpdate && handlePaymentStatusUpdate(pStatus)}
                            className={`px-3 py-2 rounded-md text-xs font-medium transition-all ${
                              orderData.payment_status === pStatus
                                ? 'bg-primary-600 text-white shadow-sm'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                          >
                            {pStatus === 'pending' ? '◷ ממתין' :
                             pStatus === 'paid' ? '✓ שולם' :
                             pStatus === 'failed' ? '✗ נכשל' : '↩ הוחזר'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Timeline/History - Shopify Style */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <ClockIcon className="h-4 w-4 text-gray-600" />
                      ציר זמן
                    </h3>
                  </div>
                  <div className="p-4 max-h-72 overflow-y-auto">
                    <div className="relative">
                      {/* Timeline Line */}
                      {(orderData.timeline || []).length > 1 && (
                        <div className="absolute right-[7px] top-3 bottom-3 w-0.5 bg-gray-200"></div>
                      )}
                      <div className="space-y-4">
                        {(orderData.timeline || []).slice().reverse().map((event, index, arr) => (
                          <div key={index} className="flex gap-3 relative">
                            <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 z-10 ${
                              index === 0 
                                ? 'bg-primary-500 border-primary-500' 
                                : 'bg-white border-gray-300'
                            }`}>
                              {index === 0 && (
                                <div className="w-full h-full flex items-center justify-center">
                                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 pb-2">
                              <p className={`text-sm ${index === 0 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                                {event.note || getStatusLabel(event.status)}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatDate(event.timestamp)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {(!orderData.timeline || orderData.timeline.length === 0) && (
                      <p className="text-sm text-gray-400 italic text-center py-4">אין היסטוריה</p>
                    )}
                  </div>
                </div>

                {/* Notes Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <DocumentTextIcon className="h-4 w-4 text-yellow-600" />
                      הערות
                    </h3>
                    {!isEditMode && (
                      <button onClick={handleEditOrder} className="text-xs text-primary-600 hover:text-primary-700">
                        {orderData.notes ? 'ערוך' : 'הוסף'}
                      </button>
                    )}
                  </div>
                  <div className="p-4">
                    {isEditMode ? (
                      <textarea
                        value={editData?.notes || ''}
                        onChange={(e) => handleEditDataChange('notes', e.target.value)}
                        placeholder="הוסף הערות להזמנה..."
                        rows={3}
                        className="w-full px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    ) : orderData.notes ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{orderData.notes}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">אין הערות</p>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-2">
                  {/* Refund Button - Show only if paid */}
                  {orderData.payment_status === 'paid' && orderData.status !== 'refunded' && (
                    <button
                      onClick={() => {
                        if (window.confirm('האם אתה בטוח שברצונך לבצע החזר כספי?')) {
                          handlePaymentStatusUpdate('refunded');
                        }
                      }}
                      className="w-full px-4 py-2.5 bg-orange-50 text-orange-700 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      💰 החזר כספי
                    </button>
                  )}
                  
                  {/* Cancel Order Button */}
                  {orderData.status !== 'cancelled' && orderData.status !== 'delivered' && (
                    <button
                      onClick={() => {
                        if (window.confirm('האם אתה בטוח שברצונך לבטל את ההזמנה?')) {
                          handleStatusUpdate('cancelled');
                        }
                      }}
                      className="w-full px-4 py-2.5 bg-red-50 text-red-600 rounded-lg border border-red-200 hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      ❌ בטל הזמנה
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </Modal>

    {/* Add Product Modal - Outside main modal to avoid nesting issues */}
    <Modal
      isOpen={showAddProductModal}
      onClose={() => {
        // Clear search timeout if exists
        if (searchTimeout) {
          clearTimeout(searchTimeout);
          setSearchTimeout(null);
        }
        setShowAddProductModal(false);
        setSelectedProductId('');
        setProductSearchTerm('');
      }}
      title="הוסף מוצר להזמנה"
      size="lg"
      zIndex={60}
    >
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={productSearchTerm}
            onChange={(e) => handleProductSearch(e.target.value)}
            placeholder="חפש מוצר לפי שם או מק״ט..."
            className="w-full pr-10 pl-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                // Trigger immediate search on Enter
                if (searchTimeout) {
                  clearTimeout(searchTimeout);
                }
                loadAvailableProducts(productSearchTerm);
              }
            }}
          />
          {productSearchTerm && (
            <button
              type="button"
              onClick={() => {
                setProductSearchTerm('');
                loadAvailableProducts(''); // Reload all products
              }}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="נקה חיפוש"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Products List */}
        {loadingProducts ? (
          <div className="text-center py-8">
            <LoadingSpinner size="md" color="primary" text="טוען מוצרים..." />
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {(() => {
              // Don't filter out products - allow adding same product as tester/bonus
              // The handleAddProduct function will check if product already exists in same type
              const filteredProducts = availableProducts;

                if (filteredProducts.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      {productSearchTerm && productSearchTerm.trim() 
                        ? `לא נמצאו מוצרים התואמים לחיפוש "${productSearchTerm}"` 
                        : 'אין מוצרים זמינים להוספה'}
                    </div>
                  );
                }

                return filteredProducts.map(product => {
                  const price = orderData?.customer?.type === 'b2b' 
                    ? (product.price_b2b || product.price_b2c || 0)
                    : (product.price_b2c || product.price_b2b || 0);
                  
                  // Check if product already exists in order
                  const existingItems = editedItems.filter(item => 
                    item.product_id === product.id && !item.removed
                  );
                  const hasRegular = existingItems.some(item => {
                    const notes = item.notes || '';
                    return !notes.includes('טסטר') && !notes.includes('בונוס');
                  });
                  const hasTester = existingItems.some(item => (item.notes || '').includes('טסטר'));
                  const hasBonus = existingItems.some(item => (item.notes || '').includes('בונוס'));
                  
                  return (
                    <div
                      key={product.id}
                      className={`p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${existingItems.length > 0 ? 'bg-blue-50/30' : ''}`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          <img
                            src={product.images?.[0] || '/placeholder-product.png'}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                            onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">
                              {product.name}
                            </h4>
                            {existingItems.length > 0 && (
                              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                קיים בהזמנה
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mb-1">
                            מק״ט: {product.sku || product.product_sku || product.SKU || 'ללא מק״ט'}
                          </p>
                          {product.brand && (
                            <p className="text-xs text-gray-500 mb-2">
                              מותג: {product.brand}
                            </p>
                          )}
                          <p className="text-sm font-medium text-primary-600">
                            {formatCurrency(price)}
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleAddProduct(product.id, {})}
                            disabled={hasRegular}
                            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                            aria-label="הוסף מוצר"
                            title={hasRegular ? 'המוצר כבר קיים בהזמנה (רגיל)' : 'הוסף מוצר'}
                          >
                            {hasRegular ? 'קיים' : 'הוסף'}
                          </button>
                          <button
                            onClick={() => handleAddProduct(product.id, { is_tester: true })}
                            disabled={hasTester}
                            className="px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center justify-center gap-1 transition-colors"
                            aria-label="הוסף טסטר"
                            title={hasTester ? 'המוצר כבר קיים בהזמנה (טסטר)' : 'הוסף טסטר'}
                          >
                            <BeakerIcon className="h-3.5 w-3.5" />
                            {hasTester ? 'קיים' : 'טסטר'}
                          </button>
                          <button
                            onClick={() => handleAddProduct(product.id, { is_bonus: true })}
                            disabled={hasBonus}
                            className="px-3 py-1.5 border border-purple-300 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center justify-center gap-1 transition-colors"
                            aria-label="הוסף בונוס"
                            title={hasBonus ? 'המוצר כבר קיים בהזמנה (בונוס)' : 'הוסף בונוס'}
                          >
                            <GiftIcon className="h-3.5 w-3.5" />
                            {hasBonus ? 'קיים' : 'בונוס'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
        )}
      </div>
    </Modal>
    </>
  );
};

export default OrderDetails;
